/**
 * Hook: Wizard state and validation for Create Organization page.
 * Single Responsibility: step, form data, validation refs, simple validators, handlePrevious.
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fieldValidators } from '../../../utils/validationSchemas';
import { TOTAL_STEPS } from '../constants';

const DEFAULT_ORGANIZATION_DATA = {
  organizationName: '',
  industry: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
};

const DEFAULT_ADMIN_DATA = {
  fullName: '',
  phoneNumber: '',
};

/**
 * Pure: check if organization form has all required fields filled (non-empty string).
 * @param {Object} data
 * @returns {boolean}
 */
export function isStep1DataValid(data) {
  return Object.values(data).every((value) => String(value).trim() !== '');
}

/**
 * Pure: check if admin form has required fullName and valid phoneNumber (non-empty + valid format).
 * Uses same phone validator as PrimaryAdministratorStep for consistency.
 * @param {Object} data
 * @returns {boolean}
 */
export function isStep2DataValid(data) {
  const fullName = (data?.fullName ?? '').trim();
  const phoneNumber = (data?.phoneNumber ?? '').trim();
  if (fullName === '' || phoneNumber === '') return false;
  const phoneError = fieldValidators.phoneNumber(phoneNumber);
  return phoneError === null;
}

/**
 * Map API organization to form shape (step 1).
 * API: { name, industry?, organizationType?, address: { street, city, state, zipCode } }
 */
function orgToStep1Data(org) {
  if (!org || typeof org !== 'object') return DEFAULT_ORGANIZATION_DATA;
  const address = org.address ?? {};
  return {
    organizationName: org.name ?? '',
    industry: org.industry ?? org.organizationType ?? '',
    street: address.street ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zipCode: address.zipCode ?? '',
  };
}

export function useCreateOrganizationWizard(initialOrganizationData = null) {
  const [searchParams] = useSearchParams();
  const stepFromUrl = Math.min(
    Math.max(1, Number.parseInt(searchParams.get('step'), 10) || 1),
    TOTAL_STEPS,
  );

  const [currentStep, setCurrentStep] = useState(stepFromUrl);
  const [organizationData, setOrganizationData] = useState(
    () => orgToStep1Data(initialOrganizationData) || DEFAULT_ORGANIZATION_DATA,
  );
  const [adminData, setAdminData] = useState(DEFAULT_ADMIN_DATA);
  const [formValidation, setFormValidation] = useState({ isValid: false, validateAll: null });
  const [adminFormValidation, setAdminFormValidation] = useState({
    isValid: false,
    validateAll: null,
  });

  const isStep1Valid = useCallback(() => isStep1DataValid(organizationData), [organizationData]);
  const isStep2Valid = useCallback(() => isStep2DataValid(adminData), [adminData]);

  const handlePrevious = useCallback(() => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToStep2 = useCallback(() => {
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    setCurrentStep((s) => (stepFromUrl !== s ? stepFromUrl : s));
  }, [stepFromUrl]);

  useEffect(() => {
    if (initialOrganizationData && typeof initialOrganizationData === 'object') {
      setOrganizationData(orgToStep1Data(initialOrganizationData));
    }
  }, [initialOrganizationData]);

  return {
    currentStep,
    setCurrentStep,
    organizationData,
    setOrganizationData,
    adminData,
    setAdminData,
    formValidation,
    setFormValidation,
    adminFormValidation,
    setAdminFormValidation,
    isStep1Valid,
    isStep2Valid,
    handlePrevious,
    goToStep2,
    totalSteps: TOTAL_STEPS,
  };
}
